import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  FileText,
  Calendar,
  Loader2,
  AlertCircle,
} from 'lucide-react';

/**
 * NewStudyModal - Create a new study with timeline milestones
 */
const NewStudyModal = ({
  isOpen,
  onClose,
  newStudyName,
  setNewStudyName,
  newStudyDescription,
  setNewStudyDescription,
  studyPhase,
  setStudyPhase,
  therapeuticArea,
  setTherapeuticArea,
  studyStartDate,
  setStudyStartDate,
  firstPatientIn,
  setFirstPatientIn,
  lastPatientIn,
  setLastPatientIn,
  lastPatientLastVisit,
  setLastPatientLastVisit,
  databaseLock,
  setDatabaseLock,
  studyCompletion,
  setStudyCompletion,
  handleCreateStudy,
  creatingStudy,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle className="text-slate-900 dark:text-slate-100">Create New Study</DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            Set up your study with timeline milestones to enable automatic document scheduling.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Basic Information
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Study Name *</Label>
                <Input placeholder="e.g., PROTOCOL-2024-001" value={newStudyName} onChange={(e) => setNewStudyName(e.target.value)} className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Phase</Label>
                <Select value={studyPhase} onValueChange={setStudyPhase}>
                  <SelectTrigger className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100">
                    <SelectValue placeholder="Select phase" />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                    <SelectItem value="Phase I">Phase I</SelectItem>
                    <SelectItem value="Phase II">Phase II</SelectItem>
                    <SelectItem value="Phase III">Phase III</SelectItem>
                    <SelectItem value="Phase IV">Phase IV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Therapeutic Area</Label>
                <Input placeholder="e.g., Oncology, Cardiology" value={therapeuticArea} onChange={(e) => setTherapeuticArea(e.target.value)} className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-slate-700 dark:text-slate-300">Description</Label>
                <Input placeholder="Brief study description" value={newStudyDescription} onChange={(e) => setNewStudyDescription(e.target.value)} className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
            </div>
          </div>

          {/* Timeline Milestones */}
          <div className="space-y-4 border-t border-slate-200 dark:border-slate-600 pt-4">
            <h4 className="font-medium text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Study Timeline Milestones
              <Badge variant="outline" className="text-xs border-slate-400 dark:border-slate-500 text-slate-500 dark:text-slate-400 ml-2">Optional</Badge>
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Setting these dates will automatically create a document schedule with due dates for required documents.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Study Start Date</Label>
                <Input type="date" value={studyStartDate} onChange={(e) => setStudyStartDate(e.target.value)} className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">First Patient In (FPI)</Label>
                <Input type="date" value={firstPatientIn} onChange={(e) => setFirstPatientIn(e.target.value)} className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Last Patient In (LPI)</Label>
                <Input type="date" value={lastPatientIn} onChange={(e) => setLastPatientIn(e.target.value)} className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Last Patient Last Visit (LPLV)</Label>
                <Input type="date" value={lastPatientLastVisit} onChange={(e) => setLastPatientLastVisit(e.target.value)} className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Database Lock</Label>
                <Input type="date" value={databaseLock} onChange={(e) => setDatabaseLock(e.target.value)} className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Study Completion</Label>
                <Input type="date" value={studyCompletion} onChange={(e) => setStudyCompletion(e.target.value)} className="bg-slate-100 dark:bg-slate-700 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Document Schedule:</strong> Based on your timeline, the system will automatically track required documents (Protocol, ICF, IRB Approval, Reports, etc.) and show them in Pending Actions when due.
              </span>
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} className="rounded-full bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200">Cancel</Button>
          <Button onClick={handleCreateStudy} disabled={creatingStudy} className="rounded-full bg-red-600 hover:bg-red-700 text-white">
            {creatingStudy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Study'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewStudyModal;
